import { useState, useEffect } from 'react';
import { adminService } from '../services/adminService';
import { Permission, UserPermissions } from '../types/admin';

interface UseAdminPermissionsReturn {
  permissions: Permission[];
  myPermissions: UserPermissions | null;
  loading: boolean;
  error: string | null;
  checkPermission: (page: string, action: string) => Promise<boolean>;
  refreshPermissions: () => Promise<void>;
  refreshMyPermissions: () => Promise<void>;
}

export const useAdminPermissions = (): UseAdminPermissionsReturn => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [myPermissions, setMyPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getPermissions();
      setPermissions(data);
    } catch (err) {
      // Silently fail if user doesn't have admin access
      // This is expected for non-admin users
      const message = err instanceof Error ? err.message : 'Failed to fetch permissions';
      console.debug('Failed to fetch all permissions (expected for non-admin users):', message);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyPermissions = async () => {
    try {
      setError(null);
      const data = await adminService.getMyPermissions();
      setMyPermissions(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch my permissions';
      console.debug('Failed to fetch my permissions:', message);
      // Don't set error for this - it's not critical for non-admin users
      setMyPermissions(null);
    }
  };

  useEffect(() => {
    fetchPermissions();
    fetchMyPermissions();
  }, []);

  const checkPermission = async (page: string, action: string): Promise<boolean> => {
    try {
      setError(null);
      return await adminService.checkPermission(page, action);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to check permission';
      setError(message);
      return false;
    }
  };

  const refreshPermissions = async () => {
    await fetchPermissions();
  };

  const refreshMyPermissions = async () => {
    await fetchMyPermissions();
  };

  return {
    permissions,
    myPermissions,
    loading,
    error,
    checkPermission,
    refreshPermissions,
    refreshMyPermissions,
  };
};

